import ExpandMore from '@mui/icons-material/ExpandMore'
import Send from '@mui/icons-material/Send'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Badge,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import React, { useState } from 'react'
import {
  useAddExclusionAreaObservationComment,
  useExclusionAreaObservationComments,
} from '../../../../hooks/data/useExclusionAreaObservations'
import { ExclusionAreaObservationCommentCard } from './ExclusionAreaObservationCommentCard'

interface ExclusionAreaObservationCommentsAccordionProps {
  observationId: string
  canComment: boolean
}

export const ExclusionAreaObservationCommentsAccordion: React.FC<
  ExclusionAreaObservationCommentsAccordionProps
> = ({ observationId, canComment }) => {
  const [expanded, setExpanded] = useState(false)
  const [newMessage, setNewMessage] = useState('')

  // Fetched as soon as the card renders rather than on expand: the collapsed summary
  // shows the comment count badge, which only exists once the comments are loaded.
  const { data: comments } = useExclusionAreaObservationComments({ observationId })
  const addComment = useAddExclusionAreaObservationComment({ observationId })

  const handleSend = () => {
    if (!newMessage.trim()) return
    // Cleared only on success so a rejected send (role gate) keeps the draft for a retry;
    // the failure itself reaches the user through the global mutation error snackbar.
    addComment.mutate(newMessage, { onSuccess: () => setNewMessage('') })
  }

  return (
    <Stack direction="column" pb={0.5} width="100%">
      <Accordion
        expanded={expanded}
        sx={{ width: '100%', boxShadow: 'none' }}
        onChange={() => setExpanded(!expanded)}
      >
        <AccordionSummary
          expandIcon={<ExpandMore />}
          sx={{
            margin: 0,
            minHeight: '30px',
            '& .MuiAccordionSummary-content': { margin: 0 },
          }}
        >
          <Typography sx={{ width: '50%', textAlign: 'start', fontSize: 14 }}>
            Comentarios
            <Badge
              badgeContent={comments?.length ?? 0}
              sx={{
                '& .MuiBadge-badge': {
                  backgroundColor: 'blue',
                  color: 'white',
                  fontSize: '0.6rem',
                  fontWeight: 'bold',
                  minWidth: '18px',
                  minHeight: '16px',
                  padding: '0 6px',
                  right: '-20px',
                },
              }}
            />
          </Typography>
        </AccordionSummary>

        <AccordionDetails>
          {comments?.length ? (
            <Paper
              elevation={0}
              sx={{
                padding: 1,
                margin: 0.5,
                borderRadius: '5px 5px 5px 5px',
                border: '1px solid #dcdcdc',
                backgroundColor: 'inherit',
              }}
            >
              {comments.map(comment => (
                <ExclusionAreaObservationCommentCard key={comment.id} comment={comment} />
              ))}
            </Paper>
          ) : null}

          {canComment && (
            <Grid container m={0.5}>
              <Grid item xs={11}>
                <TextField
                  fullWidth
                  multiline
                  disabled={addComment.isPending}
                  inputProps={{ 'aria-label': 'nuevo comentario' }}
                  rows={2}
                  value={newMessage}
                  onChange={e => setNewMessage(e.target.value)}
                />
              </Grid>
              <Grid
                item
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                xs={1}
              >
                <Button
                  aria-label="enviar comentario"
                  endIcon={
                    addComment.isPending ? (
                      <CircularProgress size={20} sx={{ color: 'white' }} />
                    ) : (
                      <Send />
                    )
                  }
                  sx={{
                    minWidth: 0,
                    maxWidth: 0,
                    '& .MuiButton-endIcon': { margin: 0 },
                  }}
                  variant="contained"
                  onClick={handleSend}
                />
              </Grid>
            </Grid>
          )}
        </AccordionDetails>
      </Accordion>
    </Stack>
  )
}
